'use client';

import MarketIcon from 'src/components/Utils/MarketIcon';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { useCallback, useMemo, useState } from 'react';
import {
	ENUM_UTILS,
	UIMarket,
	getPausedOperations,
} from '@velocity-exchange/common';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import {
	MarketType,
	BigNum,
	BASE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	AMM_RESERVE_PRECISION_EXP,
	PERCENTAGE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	BN,
	getTriggerPrice,
} from '@velocity-exchange/sdk';
import { useBaseMarketFees } from 'src/hooks/useBaseMarketFees';
import { useUpdateFundingRate } from 'src/hooks/velocityActions/settlement/useUpdateFundingRate';
import { marketHasInsuranceFund } from 'src/hooks/useInfoForCurrentlySelectedMarket';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import {
	ArrowLeft,
	ArrowRight,
	ChevronRight,
	Swap,
} from '@velocity-exchange/icons';
import { PnlLeaderboardPanel } from './PnlLeaderboardPanel';
import FundingRateCard, {
	FundingRateUnit,
	DEFAULT_FUNDING_RATE_UNIT,
} from 'src/components/MarketStatsBar/FundingRateCard';
import useBorrowLendDataForMarket from 'src/hooks/useBorrowLendDataForMarket';
import { useRolling24HVolume } from 'src/components/MarketStatsBar/Rolling24hrVolumeNumberDisplay';
import useGoToRoute from 'src/hooks/useGoToRoute';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import { formatText } from '@velocity-exchange/common/format';
import SideDrawer from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawer';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import Button from 'src/components/primitives/Button/Button';
import Divider from 'src/components/primitives/Divider/Divider';
import useIsMarketFundingPaused from 'src/hooks/useIsMarketFundingPaused';
import { useCurrentMarketInfoDisplayStore } from 'src/stores/marketLiveState/marketInfoDisplay';

const Section = ({
	sectionLabel,
	topRightNode,
	children,
}: {
	sectionLabel: string;
	topRightNode?: React.ReactNode;
	children: React.ReactNode;
}) => {
	return (
		<div className="flex flex-col gap-y-lg">
			<div className="flex w-full items-center justify-between">
				<span className="typo-label-sm text-text-default">{sectionLabel}</span>
				{topRightNode}
			</div>
			<div className="flex flex-col gap-y-sm">{children}</div>
		</div>
	);
};

const FeeDisplay = ({
	marketFees,
}: {
	marketFees: {
		takerFeePct: number;
		makerFeePct: number;
		isZeroFeesPromotion: boolean;
		actualTakerFeePct?: number;
		actualMakerFeePct?: number;
	};
}) => {
	if (marketFees.isZeroFeesPromotion) {
		// Use actualTakerFeePct/actualMakerFeePct if available, otherwise fallback to regular fees
		const displayTakerFee =
			marketFees.actualTakerFeePct ?? marketFees.takerFeePct;
		const displayMakerFee =
			marketFees.actualMakerFeePct ?? marketFees.makerFeePct;

		return (
			<>
				<span className="text-text-secondary line-through">
					{`${parseFloat(displayTakerFee.toFixed(5))}% / ${parseFloat(
						Math.abs(displayMakerFee).toFixed(5)
					)}%`}
				</span>{' '}
				<span className="text-star-yellow">0% / 0%</span>
			</>
		);
	}

	return `${parseFloat(marketFees.takerFeePct.toFixed(5))}% / ${parseFloat(
		Math.abs(marketFees.makerFeePct).toFixed(5)
	)}%`;
};

const DetailRow = ({
	label,
	value,
	marketSymbol,
}: {
	label: React.ReactNode;
	value: React.ReactNode;
	marketSymbol?: string;
}) => (
	<div className="flex w-full justify-between">
		<span className="typo-body-xs text-text-secondary">{label}</span>
		<div className="flex items-center gap-1">
			{marketSymbol && (
				<MarketIcon marketSymbol={marketSymbol} sizeClass="size-4" />
			)}
			<span className="flex items-center gap-x-xs typo-body-xs text-text-default">
				{value}
			</span>
		</div>
	</div>
);

const PerpMarketDetailsSection = ({
	market,
	showPnl = false,
	onClose,
}: {
	market: UIMarket;
	showPnl?: boolean;
	onClose?: () => void;
}) => {
	const selectedMarketId = market.marketId;
	const oraclePrice = useOraclePrice(market.marketId);
	const { updateFundingRate } = useUpdateFundingRate();
	const getTickSizeForMarket = useGetTickSizeForMarket();
	const goToRoute = useGoToRoute();

	const marketInfo = useMarketsInfoStore(
		(s) => s.allMarketsInfo.perpLookup[market.marketIndex]
	);

	const marketAccount = marketInfo?.account;

	// Calculate trigger price using SDK function
	const triggerPrice: BigNum | undefined = useMemo(() => {
		if (marketAccount && oraclePrice) {
			try {
				const now = new BN(Math.floor(Date.now() / 1000)); // Current timestamp in seconds (integer)
				const triggerPriceBN = getTriggerPrice(
					marketAccount,
					oraclePrice.val,
					now,
					true // useMedianPrice = false for standard calculation
				);
				return new BigNum(triggerPriceBN, PRICE_PRECISION_EXP);
			} catch (error) {
				console.warn('Error calculating trigger price:', error);
			}
		}
		return undefined;
	}, [marketAccount, oraclePrice]);

	const marketData = useCurrentMarketInfoDisplayStore((s) => s.data);

	const marketFees = useBaseMarketFees({
		marketType: MarketType.PERP,
		marketIndex: market.marketIndex,
	});

	const imfFactor = marketAccount?.imfFactor;
	const orderTickSize = marketAccount?.orderTickSize;
	const orderStepSize = marketAccount?.orderStepSize;

	// Calculate tick size decimal places for precise price formatting
	const tickSize = getTickSizeForMarket(selectedMarketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const extendedInfo = useMarketsInfoStore(
		(s) => s.allMarketsInfo.perpLookup[market.marketIndex]?.extendedInfo
	);

	const [fundingRateUnit, setFundingRateUnit] =
		useSyncLocalStorage<FundingRateUnit>(
			'fundingRateUnitPreference',
			DEFAULT_FUNDING_RATE_UNIT
		);

	const [showPnlLeaders, setShowPnlLeaders] = useState(showPnl);

	const { volume } = useRolling24HVolume(selectedMarketId);

	const { minOrderSize, marginMaintenance, availableInsurance, pnlPoolValue } =
		extendedInfo || {};

	const displayMinOrderSize = minOrderSize
		? BigNum.from(minOrderSize, BASE_PRECISION_EXP).prettyPrint()
		: null;
	const displayOrderTickSize = orderTickSize
		? BigNum.from(orderTickSize, PRICE_PRECISION_EXP).prettyPrint()
		: null;
	const displayOrderStepSize = BigNum.from(
		orderStepSize,
		AMM_RESERVE_PRECISION_EXP
	).prettyPrint();

	const displayImfFactor = imfFactor
		? BigNum.from(imfFactor, PERCENTAGE_PRECISION_EXP).prettyPrint()
		: null;
	const displayMarginMaintenance = BigNum.from(
		(marginMaintenance / 100) * 10 ** PERCENTAGE_PRECISION_EXP.toNumber(),
		PERCENTAGE_PRECISION_EXP
	).prettyPrint();
	const displayAvailableInsurance = availableInsurance
		? BigNum.from(availableInsurance, QUOTE_PRECISION_EXP).toMillified()
		: null;
	const displayPnlPoolValue = pnlPoolValue
		? `$${BigNum.from(pnlPoolValue, QUOTE_PRECISION_EXP).toMillified()}`
		: null;

	const displayLiquidationFee = `${
		(marketAccount.liquidatorFee + marketAccount.ifLiquidationFee) / 10000
	}%`;

	const hasLimitedInsurance = !marketHasInsuranceFund(marketAccount);

	const fundingIsDue =
		marketData && marketData?.estimatedFundingRateCountdownTs <= 0;

	const updateFundingRateCb = () => updateFundingRate(market.marketIndex);

	const goToFundingHistory = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onClose?.();
		goToRoute(`/stats/funding-history?market=${market.uiSymbols.marketSymbol}`);
	};

	const openPnlLeaderboard = () => setShowPnlLeaders(true);

	const closePnlLeaderboard = () => setShowPnlLeaders(false);

	const toggleFundingRateUnit = useCallback(() => {
		setFundingRateUnit(fundingRateUnit === 'h' ? 'y' : 'h');
	}, [fundingRateUnit]);

	const pausedOperations = getPausedOperations(marketAccount);
	const isFundingPaused = useIsMarketFundingPaused(market.marketId);

	return showPnlLeaders ? (
		<PnlLeaderboardPanel
			marketIndex={market.marketIndex}
			poolBalanceDisplay={displayPnlPoolValue ?? ''}
			goBackComponent={
				<div className="flex items-center gap-x-xs">
					<IconButton
						size="sm"
						variant="tertiary"
						icon={ArrowLeft}
						onClick={closePnlLeaderboard}
					/>
					<span className="typo-label-sm text-text-default">P&L Pool</span>
				</div>
			}
		/>
	) : (
		<>
			<Section sectionLabel="Market Details">
				<DetailRow
					label="Oracle Price"
					value={
						oraclePrice
							? `$${formatText(oraclePrice.toNum(), {
									digits: {
										kind: 'decimals',
										decimals: tickSizeDecimalPlaces,
										rounding: 'half-ceil',
									},
								})}`
							: undefined
					}
				/>
				<DetailRow
					label={
						<DefinitionTooltip definitionId="tradingReferencePrice">
							Reference Price
						</DefinitionTooltip>
					}
					value={
						triggerPrice
							? `$${formatText(triggerPrice.toNum(), {
									digits: {
										kind: 'decimals',
										decimals: tickSizeDecimalPlaces,
										rounding: 'half-ceil',
									},
								})}`
							: undefined
					}
				/>
				<DetailRow
					label="P&L Pool"
					value={
						<>
							{displayPnlPoolValue}
							<IconButton
								size="sm"
								variant="tertiary"
								className="-m-sm p-sm"
								icon={ChevronRight}
								onClick={openPnlLeaderboard}
							/>
						</>
					}
				/>
				<DetailRow
					label={
						<DefinitionTooltip
							definitionId="tradingLimitedInsuranceFund"
							disabled={!hasLimitedInsurance}
						>
							Insurance Fund
						</DefinitionTooltip>
					}
					value={displayAvailableInsurance}
				/>
				<DetailRow
					label="Long Open Interest"
					value={`${marketData?.longOpenInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}
				/>
				<DetailRow
					label="Short Open Interest"
					value={`${marketData?.shortOpenInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}
				/>
				<DetailRow
					label="Max Open Interest"
					value={`${marketData?.maxOpenInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}
				/>
				<DetailRow
					label="24h Volume"
					value={`$${BigNum.from(volume).toMillified()}`}
				/>
				<DetailRow
					label="Market Status"
					value={
						<span className="capitalize">
							{ENUM_UTILS.toStr(marketAccount.status)}
						</span>
					}
				/>
				<DetailRow
					label="Paused Operations"
					value={
						<span className="capitalize">
							{pausedOperations?.join(', ') || 'None'}
						</span>
					}
				/>
			</Section>
			<>
				<Divider />
				<Section
					sectionLabel="Funding Rate"
					topRightNode={
						<TextButton
							size="sm"
							variant="secondary"
							onClick={goToFundingHistory}
							rightIcon={ArrowRight}
						>
							View history
						</TextButton>
					}
				>
					<DetailRow
						label={
							<span className="flex items-center gap-x-xs">
								Predicted / Last 24h
								<IconButton
									size="sm"
									variant="tertiary"
									icon={Swap}
									onClick={toggleFundingRateUnit}
								/>
							</span>
						}
						value={
							<FundingRateCard
								marketSymbol={market.uiSymbols.marketSymbol}
								selectedMarketId={selectedMarketId}
								textOnlyDisplay
							/>
						}
					/>

					<DetailRow
						label="Next funding update"
						value={
							isFundingPaused ? (
								'-'
							) : (
								<span className="flex items-center gap-x-xs">
									{fundingIsDue && (
										<Button
											size="sm"
											variant="secondary"
											onClick={updateFundingRateCb}
										>
											Trigger Update
										</Button>
									)}
									{marketData &&
										(fundingIsDue
											? 'Due*'
											: `${Math.floor(
													marketData?.estimatedFundingRateCountdownTs / 60
												)
													.toString()
													.padStart(2, '0')}:${(
													marketData?.estimatedFundingRateCountdownTs % 60
												)
													.toString()
													.padStart(2, '0')}`)}
								</span>
							)
						}
					/>
				</Section>
			</>
			<Divider />
			<Section sectionLabel="Market Rules">
				<DetailRow
					label="Taker Fee / Maker Rebate"
					value={<FeeDisplay marketFees={marketFees} />}
				/>
				<DetailRow label="Min. Order Size" value={displayMinOrderSize} />
				<DetailRow
					label={
						<DefinitionTooltip definitionId="tradingOrderTickSize">
							Order Tick Size
						</DefinitionTooltip>
					}
					value={displayOrderTickSize}
				/>
				<DetailRow
					label={
						<DefinitionTooltip definitionId="tradingOrderStepSize">
							Order Step Size
						</DefinitionTooltip>
					}
					value={displayOrderStepSize}
				/>
				<DetailRow label="IMF Factor" value={displayImfFactor} />
				<DetailRow
					label="Margin Maintenance"
					value={`${displayMarginMaintenance}%`}
				/>
				<DetailRow
					label="Total Liquidation Fee"
					value={displayLiquidationFee}
				/>
				<DetailRow label="Market Index" value={market.marketIndex} />
			</Section>
		</>
	);
};

const SpotMarketDetailsSection = ({
	market,
}: //onClose,
{
	market: UIMarket;
	//onClose?: () => void;
}) => {
	const getOraclePrice = useGetOraclePriceForMarket();
	const selectedMarketId = market.marketId;
	const getTickSizeForMarket = useGetTickSizeForMarket();

	const oraclePrice = getOraclePrice(market.marketId);
	// const isMobile = useIsMobileScreenSize();

	const marketInfo = useMarketsInfoStore(
		(s) => s.allMarketsInfo.spotLookup[market.marketIndex]
	);

	const marketAccount = marketInfo?.account;

	const borrowLendData = useBorrowLendDataForMarket(selectedMarketId);

	const marketFees = useBaseMarketFees({
		marketType: MarketType.SPOT,
		marketIndex: market.marketIndex,
	});

	const imfFactor = marketAccount?.imfFactor;
	const orderTickSize = marketAccount?.orderTickSize;
	const orderStepSize = marketAccount?.orderStepSize;
	const minOrderSize = marketAccount?.minOrderSize;
	const marketPrecisionExp = marketInfo.config.precisionExp;

	// Calculate tick size decimal places for precise price formatting
	const tickSize = getTickSizeForMarket(selectedMarketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const displayMinOrderSize = minOrderSize
		? BigNum.from(minOrderSize, marketPrecisionExp).prettyPrint()
		: null;
	const displayOrderTickSize = orderTickSize
		? BigNum.from(orderTickSize, QUOTE_PRECISION_EXP).prettyPrint()
		: null;
	const displayOrderStepSize = BigNum.from(
		orderStepSize,
		marketPrecisionExp
	).prettyPrint();

	const displayImfFactor = imfFactor
		? BigNum.from(imfFactor, PERCENTAGE_PRECISION_EXP).prettyPrint()
		: null;

	const pausedOperations = getPausedOperations(marketAccount);

	return (
		<>
			<Section sectionLabel="Market Details">
				<DetailRow
					label="Oracle Price"
					value={
						oraclePrice
							? `$${formatText(oraclePrice.toNum(), {
									digits: {
										kind: 'decimals',
										decimals: tickSizeDecimalPlaces,
										rounding: 'half-ceil',
									},
								})}`
							: undefined
					}
				/>

				<DetailRow
					label="Available to Borrow"
					value={`${borrowLendData?.borrowLimitBase?.prettyPrint(
						false,
						marketPrecisionExp.toNumber()
					)} ${marketInfo.genericInfo.baseAssetSymbol}`}
				/>

				<DetailRow
					label="Market Status"
					value={
						<span className="capitalize">
							{ENUM_UTILS.toStr(marketAccount.status)}
						</span>
					}
				/>
				<DetailRow
					label="Paused Operations"
					value={
						<span className="capitalize">
							{pausedOperations?.join(', ') || 'None'}
						</span>
					}
				/>
			</Section>
			<Divider />
			<Section sectionLabel="Market Rules">
				<DetailRow
					label="Taker Fee / Maker Rebate"
					value={<FeeDisplay marketFees={marketFees} />}
				/>
				<DetailRow label="Min. Order Size" value={displayMinOrderSize} />
				<DetailRow
					label={
						<DefinitionTooltip definitionId="tradingOrderTickSize">
							Order Tick Size
						</DefinitionTooltip>
					}
					value={displayOrderTickSize}
				/>
				<DetailRow
					label={
						<DefinitionTooltip definitionId="tradingOrderStepSize">
							Order Step Size
						</DefinitionTooltip>
					}
					value={displayOrderStepSize}
				/>
				<DetailRow label="IMF Factor" value={displayImfFactor} />
				<DetailRow
					label="Initial / Maintenance Asset Weight"
					value={`${borrowLendData?.assetWeight}% / ${borrowLendData?.maintenanceAssetWeight}%`}
				/>
				<DetailRow
					label="Initial / Maintenance Liability Weight"
					value={`${borrowLendData?.liabilityWeight}% / ${borrowLendData?.maintenanceLiabilityWeight}%`}
				/>
				<DetailRow label="Market Index" value={market.marketIndex} />
			</Section>
		</>
	);
};

export const MarketDetailsDrawer = () => {
	const handleCloseHistoryPanel = useDrawersStore((s) => s.handleCloseDrawer);
	const { uiMarket, goToPnl } = useDrawersStore((s) => s.marketDetails);

	const symbol = uiMarket?.market.symbol;
	const isSpot = uiMarket?.isSpot;

	const onClose = () => {
		handleCloseHistoryPanel('marketDetails');
	};

	if (!uiMarket) {
		return null;
	}

	return (
		<SideDrawer.Container drawerName="marketDetails">
			<SideDrawer.Title>
				<MarketIcon marketSymbol={symbol ?? ''} />
				<span>{symbol}</span>
			</SideDrawer.Title>
			<SideDrawer.Body>
				{isSpot && <SpotMarketDetailsSection market={uiMarket} />}
				{!isSpot && (
					<PerpMarketDetailsSection
						market={uiMarket}
						showPnl={goToPnl}
						onClose={onClose}
					/>
				)}
			</SideDrawer.Body>
		</SideDrawer.Container>
	);
};
